import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Container,
  Link,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const LoadingComponent = ({ queueId, callback }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const pollStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_ALUDRA_API}/registerv2/register/${queueId}/status`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(t("loadingComponent.networkError"));
        }

        const result = await response.json();
        console.log("Status response:", result.Data); // Debug log
        setData(result.Data);

        if (
          result.Data.Status === "PROCESSING" ||
          result.Data.Status === "QUEUED"
        ) {
          setTimeout(pollStatus, 2000);
        } else if (result.Data.Status === "FAILED") {
          setError(true);
        } else {
          callback(false);
        }
      } catch (err) {
        setError(err.message);
        callback(false);
      }
    };

    if (queueId) {
      pollStatus();
    }

    // Cleanup function
    return () => {
      // If there's an active timeout, it will be cleared
      const clearTimeouts = () => {
        const highestTimeoutId = setTimeout(() => {});
        for (let i = 0; i < highestTimeoutId; i++) {
          clearTimeout(i);
        }
      };
      clearTimeouts();
    };
  }, [queueId, callback, t]);

  // Error State
  if (error) {
    return (
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 3,
            textAlign: "center",
            backgroundColor: (theme) => theme.palette.error.light,
            color: (theme) => theme.palette.error.contrastText,
          }}
        >
          <Typography variant="h5" gutterBottom>
            {t("loadingComponent.errorTitle")}
          </Typography>
          <Typography variant="body1">
            {t("loadingComponent.errorMessage")}
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Initial Loading State
  if (!data) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">
            {t("loadingComponent.initialLoading")}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Progress State
  const progress =
    data.TotalSteps > 0 ? (data.CurrentStep / data.TotalSteps) * 100 : 0;

  return (
    <Container maxWidth="sm">
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CircularProgress size={30} sx={{ mr: 2 }} />
            <Typography variant="h6">
              {t("loadingComponent.processingRequest")}
            </Typography>
          </Box>
          <Typography variant="body2">
            {t("loadingComponent.stepCounter", {
              currentStep: data.CurrentStep || 1,
              totalSteps: data.TotalSteps || 10,
            })}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            width: "100%",
            height: 10,
            borderRadius: 5,
            mb: 2,
          }}
        />

        <Typography
          variant="body1"
          sx={{
            textAlign: "center",
            fontStyle: "italic",
          }}
        >
          {data.CurrentStepDescription || t("loadingComponent.initialLoading")}
        </Typography>
        <Box sx={{ pt: 2, display: "flex" }}>
          <Typography sx={{ pr: 1 }} variant="body1">
            {t("support.haveQuestions")}
          </Typography>
          <Link
            target="_blank"
            href={process.env.REACT_APP_SUPPORT_PAGE}
            variant="body1"
          >
            {t("support.visitSite")}
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoadingComponent;
